import React from "react";
import { LegalPage, LegalSection, LegalList } from "@/components/LegalPage";

const Privacy = () => {
  return (
    <LegalPage
      overline="Legal"
      title="Privacy Policy"
      effectiveDate="March 2026"
      testId="privacy-page"
    >
      <p>
        This Privacy Policy explains how <strong className="text-navy">Taonga OÜ</strong> processes
        personal data in connection with this website and direct business
        correspondence. We process personal data in accordance with the EU
        General Data Protection Regulation (Regulation (EU) 2016/679 — "GDPR")
        and the Estonian Personal Data Protection Act.
      </p>

      <LegalSection number={1} title="Data Controller" testId="privacy-controller">
        <p>The controller of personal data processed under this policy is:</p>
        <div className="bg-soft border border-border rounded-md p-5 text-[14.5px]">
          <div className="font-semibold text-navy">Taonga OÜ</div>
          <div>A. Weizenbergi tn 16, 10150, Tallinn, Estonia</div>
          <div>
            Email:{" "}
            <a href="mailto:info@taonga.eu" className="text-navy hover:text-warm transition-colors">
              info@taonga.eu
            </a>
          </div>
        </div>
      </LegalSection>

      <LegalSection number={2} title="What Information We Process">
        <p>
          We only process personal data that you voluntarily submit through
          direct correspondence (for example via our contact form, which uses
          your own email client, or by emailing us directly). This typically
          includes:
        </p>
        <LegalList
          items={[
            "Identification data — name and, where applicable, company name",
            "Contact data — email address",
            "Content of your message — any information you choose to share with us",
          ]}
        />
        <p>
          The website itself does not run analytics, marketing or tracking
          technologies and does not automatically collect personal data from
          visitors beyond what is strictly necessary for the site to function.
        </p>
      </LegalSection>

      <LegalSection number={3} title="Lawful Basis">
        <LegalList
          items={[
            "Legitimate interests (Art. 6(1)(f) GDPR) — for managing direct business correspondence and pre-contractual enquiries",
            "Performance of, or steps prior to, a contract (Art. 6(1)(b) GDPR) — where applicable",
            "Compliance with legal obligations (Art. 6(1)(c) GDPR) — for accounting and statutory record-keeping",
          ]}
        />
      </LegalSection>

      <LegalSection number={4} title="How We Use Personal Data">
        <LegalList
          items={[
            "To respond to your enquiry and manage business correspondence",
            "To evaluate and develop potential business relationships",
            "To comply with applicable legal, accounting and regulatory obligations",
          ]}
        />
        <p>
          We do not sell personal data, and we do not use personal data for
          automated decision-making or profiling.
        </p>
      </LegalSection>

      <LegalSection number={5} title="Sharing With Third Parties">
        <p>
          Personal data may be shared with carefully selected service providers
          acting on our behalf (for example email, hosting and IT providers)
          and with competent public authorities where required by law. Any such
          sharing is limited to what is necessary and is governed by
          appropriate contractual safeguards.
        </p>
      </LegalSection>

      <LegalSection number={6} title="International Transfers">
        <p>
          Personal data is primarily stored and processed within the European
          Economic Area (EEA). Where a transfer outside the EEA is necessary,
          we rely on appropriate safeguards under Chapter V of the GDPR
          (including, where applicable, Standard Contractual Clauses).
        </p>
      </LegalSection>

      <LegalSection number={7} title="Retention">
        <p>
          We retain personal data only for as long as necessary to fulfil the
          purposes set out in this policy, to comply with legal obligations
          (for example a 7-year retention period under Estonian accounting
          law), or to establish, exercise or defend legal claims. Business
          correspondence is generally retained for up to 24 months unless a
          longer period is required.
        </p>
      </LegalSection>

      <LegalSection number={8} title="Your Rights" testId="privacy-rights">
        <p>Subject to the conditions of the GDPR, you have the right to:</p>
        <LegalList
          items={[
            "Access the personal data we hold about you",
            "Request rectification of inaccurate or incomplete data",
            "Request erasure of your data ('right to be forgotten')",
            "Request restriction of processing",
            "Object to processing based on legitimate interests",
            "Data portability, where applicable",
            "Withdraw consent at any time, where processing is based on consent",
            "Lodge a complaint with the Estonian Data Protection Inspectorate (Andmekaitse Inspektsioon)",
          ]}
        />
        <p>
          To exercise any of these rights, please contact us at{" "}
          <a
            href="mailto:info@taonga.eu"
            className="text-navy hover:text-warm transition-colors"
          >
            info@taonga.eu
          </a>
          . We respond to requests within one month, as required by the GDPR.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Security">
        <p>
          We apply appropriate technical and organisational measures designed
          to protect personal data against unauthorised access, alteration,
          disclosure or destruction. However, no method of electronic
          transmission or storage is fully secure.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Children">
        <p>
          This website is intended for a professional and corporate audience
          and is not directed to children. We do not knowingly collect personal
          data from individuals under the age of 16.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Updates">
        <p>
          We may update this Privacy Policy from time to time to reflect
          changes in our activities or applicable law. The latest version will
          always be available on this page with the updated effective date.
        </p>
      </LegalSection>

      <LegalSection number={12} title="Contact">
        <p>
          For any questions or requests relating to this policy, please write
          to{" "}
          <a
            href="mailto:info@taonga.eu"
            className="text-navy hover:text-warm transition-colors"
          >
            info@taonga.eu
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default Privacy;
